import React, { useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../libs/contextLib";
import { publicScheduleModelData, scheduleModelData } from "../../services/ModelService";
import { values } from "lodash";
import { Schedule, PREDEFINED_SCHEDULES } from "../../model/schedule";
import GanttView from "./GanttView";
import { FormControl, InputGroup } from "react-bootstrap";
import LoadMessage from "../../components/LoadMessage";
import { Input, Select, Button, Tooltip } from 'antd';
import { SearchOutlined, PlusOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { LinkContainer } from 'react-router-bootstrap';
import "./CalendarV2.css";
import dayjs from "dayjs";
import range from 'lodash/range';
import { ulid } from 'ulid';
import { FilterSwitches } from "../../containers/FilterTag";
import { filterFn } from '../../containers/FilterTag';

const filterItems = {
    Private: {
        name: 'Private',
        color: '#1890ff',
        showFn: s => !s.shared,
        on: true
    },
    Shared: {
        name: 'Public',
        color: '#52c41a',
        showFn: s => s.shared
    },
    Active: {
        showFn: (s) => s.shared || (!s?.isObsolete() && !s?.isFuture())
    },
    Future: {
        showFn: (s) => !s.shared && s?.isFuture()
    },
    Expired: {
        showFn: (s) => !s.shared && s?.isObsolete()
    },
    Warning: {
        showFn: (s) => s.warning?.length
    }
};

function CalendarV2() {
    const { privateSchedules, setPrivateSchedules, publicSchedules, setPublicSchedules, isAuthenticating, isAuthenticated, isAdmin } = useAppContext();
    const [filter, setFilter] = useState("");
    const [sortBy, setSortBy] = useState('name');
    const [isLoading, setIsLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [selectedDates, setSelectedDates] = useState([]);
    const [pinnedCalendars, setPinnedCalendars] = useState([]);
    const currentYear = dayjs().year();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1); // months are 1-12 for user display
    const [filterBy, setFilterBy] = useState(['Private', 'Active']);
    const [allTags, setAllTags] = useState(new Set());
    // Calculate start and end of year
    const startDate = useMemo(() => dayjs(`${selectedYear}-01-01`).startOf('day'), [selectedYear]);
    const endDate = useMemo(() => dayjs(`${selectedYear}-12-31`).endOf('day'), [selectedYear]);
    const numberOfDays = useMemo(() => endDate.diff(startDate, 'day') + 1, [startDate, endDate]);
    // Calculate scroll to date based on selected month
    const scrollToDate = dayjs(`${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`).startOf('day');
    const [expandedScheduleId, setExpandedScheduleId] = useState(null);
    const [editModeScheduleId, setEditModeScheduleId] = useState(null);
    const [editingCalendars, setEditingCalendars] = useState({});
    const [manualScroll, setManualScroll] = useState(false);

    // Memoize filtered/mapped schedules, excluding pinnedCalendars from dependencies
    const ganttSchedules = useMemo(() => {
        if (!privateSchedules && publicSchedules) return [];
        // Combine user schedules with predefined schedules
        const allSchedules = values({
            ...privateSchedules,
            ...publicSchedules,
            ...PREDEFINED_SCHEDULES,
        });
        // First apply text search filter
        const textFiltered = filter
            ? allSchedules.filter(s =>
                (s.scheduleName && s.scheduleName.toLowerCase().indexOf(filter.toLowerCase()) > -1)
            )
            : allSchedules;
        // Use the exported filterFn for filtering
        const filtered = filterFn(textFiltered, filterItems, filterBy, 'scheduleId', undefined);
        // Convert schedules to a format compatible with GanttView
        return filtered.filter(s => !s.charter).map(schedule => {
            const allDates = schedule.schedulePeriods?.reduce((acc, period) => {
                if (period.startDate && period.endDate) {
                    const startDate = dayjs(period.startDate).startOf('day');
                    const endDate = dayjs(period.endDate).endOf('day');
                    acc.push(startDate);
                    acc.push(endDate);
                }
                return acc;
            }, []) || [];
            const today = dayjs().startOf('day');
            const tomorrow = today.add(1, 'day').endOf('day');
            const startTime = allDates.length ? allDates.reduce((min, date) => date.isBefore(min) ? date : min, allDates[0]) : today;
            const endTime = allDates.length ? allDates.reduce((max, date) => date.isAfter(max) ? date : max, allDates[0]) : tomorrow;
            return {
                ...schedule,
                shiftBatId: schedule.scheduleId,
                shiftBatNumber: schedule.scheduleName,
                shiftBatName: schedule.scheduleDetails || 'No details',
                shiftBatColour: schedule.shared ? filterItems.Shared.color : filterItems.Private.color,
                shiftBatLogo: schedule.scheduleLogo,
                getStartTime: () => startTime.toDate(),
                getEndTime: () => endTime.toDate(),
                getShiftTime: () => endTime.diff(startTime, 'seconds'),
                rows: schedule.schedulePeriods?.map((period, index) => {
                    if (!period.startDate || !period.endDate) return null;
                    const periodStart = dayjs(period.startDate).startOf('day');
                    const periodEnd = dayjs(period.endDate).endOf('day');
                    return {
                        id: `${schedule.scheduleId}-${index}`,
                        type: 'period',
                        title: period.toString(),
                        getTime: () => periodStart.toDate(),
                        getEndTime: () => periodEnd.toDate(),
                        duration: periodEnd.diff(periodStart, 'seconds'),
                        route: {
                            routeNumber: period.toString(),
                            routeShortName: period.toString()
                        }
                    };
                }).filter(Boolean) || []
            };
        });
    }, [privateSchedules, publicSchedules, filter, filterBy, editModeScheduleId]);

    useEffect(() => {
        if (!privateSchedules && publicSchedules) return;

        // Combine user schedules with predefined schedules
        const allSchedules = values({
            ...privateSchedules,
            ...PREDEFINED_SCHEDULES,
            ...publicSchedules
        });

        // First apply text search filter
        const textFiltered = filter
            ? allSchedules.filter(s =>
                (s.scheduleName && s.scheduleName.toLowerCase().indexOf(filter.toLowerCase()) > -1)
            )
            : allSchedules;

        // Use the exported filterFn for filtering
        const filtered = filterFn(textFiltered, filterItems, filterBy, 'scheduleId', undefined);

        // Convert schedules to a format compatible with GanttView
        const ganttSchedules = filtered.filter(s => !s.charter).map(schedule => {
            // Get the first and last dates from all periods
            const allDates = schedule.schedulePeriods?.reduce((acc, period) => {
                if (period.startDate && period.endDate) {
                    // Convert to dayjs for consistent date handling
                    const startDate = dayjs(period.startDate).startOf('day');
                    const endDate = dayjs(period.endDate).endOf('day');
                    acc.push(startDate);
                    acc.push(endDate);
                }
                return acc;
            }, []) || [];

            // Default to today's date if no valid dates found
            const today = dayjs().startOf('day');
            const tomorrow = today.add(1, 'day').endOf('day');

            // Find the earliest start date and latest end date
            const startTime = allDates.length ? allDates.reduce((min, date) => date.isBefore(min) ? date : min, allDates[0]) : today;
            const endTime = allDates.length ? allDates.reduce((max, date) => date.isAfter(max) ? date : max, allDates[0]) : tomorrow;

            // Convert to ShiftBat format
            return {
                ...schedule,
                shiftBatId: schedule.scheduleId,
                shiftBatNumber: schedule.scheduleName,
                shiftBatName: schedule.scheduleDetails || 'No details',
                shiftBatColour: schedule.shared ? filterItems.Shared.color : filterItems.Private.color,
                shiftBatLogo: schedule.scheduleLogo,
                getStartTime: () => startTime.toDate(),
                getEndTime: () => endTime.toDate(),
                getShiftTime: () => endTime.diff(startTime, 'seconds'),
                rows: schedule.schedulePeriods?.map((period, index) => {
                    if (!period.startDate || !period.endDate) return null;

                    const periodStart = dayjs(period.startDate).startOf('day');
                    const periodEnd = dayjs(period.endDate).endOf('day');

                    return {
                        id: `${schedule.scheduleId}-${index}`,
                        type: 'period',
                        title: period.toString(),
                        getTime: () => periodStart.toDate(),
                        getEndTime: () => periodEnd.toDate(),
                        duration: periodEnd.diff(periodStart, 'seconds'),
                        route: {
                            routeNumber: period.toString(),
                            routeShortName: period.toString()
                        }
                    };
                }).filter(Boolean) || [] // Filter out null periods
            };
        });

        // Collect all unique tags from schedules
        const tags = new Set();
        if (!privateSchedules) return;
        Object.values(privateSchedules).forEach(schedule => {
            schedule.tags?.forEach(tag => tags.add(tag));
        });
        setAllTags(tags);
    }, [privateSchedules, publicSchedules, filter, filterBy, editModeScheduleId]);

    const sortSchedules = (schedules, sortType) => {
        if (!schedules) return [];
        const sorted = Object.values(schedules).sort((a, b) => {
            switch (sortType) {
                case 'name':
                    return a.scheduleName.localeCompare(b.scheduleName);
                case 'startDate':
                    return dayjs(a.getStartTime?.()).diff(dayjs(b.getStartTime?.()));
                case 'endDate':
                    return dayjs(a.getEndTime?.()).diff(dayjs(b.getEndTime?.()));
                default:
                    return 0;
            }
        });

        // Filter by text search
        const filtered = filter ? sorted.filter(schedule => {
            const searchStr = filter.toLowerCase();
            return (
                schedule.scheduleName.toLowerCase().includes(searchStr) ||
                schedule.tags?.some(tag => tag.toLowerCase().includes(searchStr))
            );
        }) : sorted;

        // Filter by status
        const statusFiltered = filterBy.length ? filtered.filter(schedule => {
            return filterBy.some(key => filterItems[key].showFn(schedule));
        }) : filtered;

        return statusFiltered;
    };

    // Handle sort change
    const handleSortChange = (value) => {
        setSortBy(value);
    };

    // Handle search
    const onSearch = (value) => {
        setFilter(value);
    };

    // Handle month navigation arrows
    const handleMonthArrow = (direction) => {
        if (direction === 'left') {
            if (selectedMonth === 1) {
                setSelectedMonth(12);
                setSelectedYear(selectedYear - 1);
            } else {
                setSelectedMonth(selectedMonth - 1);
            }
        } else {
            if (selectedMonth === 12) {
                setSelectedMonth(1);
                setSelectedYear(selectedYear + 1);
            } else {
                setSelectedMonth(selectedMonth + 1);
            }
        }
    };

    // check if the public calendar filter is active and if the user is an admin
    const isInPublicAndAdmin = useMemo(() => {
        return filterBy.includes('Shared') && isAdmin;
    }, [filterBy, isAdmin]);

    const addNewSchedule = () => {
        const scheduleId = ulid();
        const newSchedule = new Schedule({ scheduleId, publicScheduleId: scheduleId, scheduleName: '', schedulePeriods: [], operatingDays: [true, true, true, true, true, false, false], includedSchedules: [], excludedSchedules: [], exceptions: [], shared: isInPublicAndAdmin });
        if(isInPublicAndAdmin) {
            setPublicSchedules(publicSchedules => ({ [newSchedule.scheduleId]: newSchedule, ...publicSchedules }));
        } else {
            setPrivateSchedules(privateSchedules => ({ [newSchedule.scheduleId]: newSchedule, ...privateSchedules }));
        }
        setExpandedScheduleId(newSchedule.scheduleId);
        setEditModeScheduleId(newSchedule.scheduleId);
    };

    // Handler for saving a schedule (new or existing)
    const handleScheduleUpdate = (updatedSchedule) => {
        setPrivateSchedules(privateSchedules => ({ ...privateSchedules, [updatedSchedule.scheduleId]: updatedSchedule }));
        // Keep the calendar expanded but switch to view mode
        setExpandedScheduleId(updatedSchedule.scheduleId);
        setEditModeScheduleId(null);
    };

    // Handler for cancelling edit
    const handleCancelEdit = (scheduleId) => {
        // If it's a new/empty schedule, remove it
        if (!privateSchedules[scheduleId] || (!privateSchedules[scheduleId].scheduleName && (!privateSchedules[scheduleId].schedulePeriods || privateSchedules[scheduleId].schedulePeriods.length === 0))) {
            setPrivateSchedules(privateSchedules => {
                const copy = { ...privateSchedules };
                delete copy[scheduleId];
                return copy;
            });
            setExpandedScheduleId(null);
        } else {
            // Keep the calendar expanded but switch to view mode
            setExpandedScheduleId(scheduleId);
        }
    };

    function renderCalendars() {
        return (
            <>
                {!isLoading && !fetching ? (
                    <div className="routes">
                        <div className="d-flex align-items-center justify-content-between filter-options-main">
                            <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                                {/* Show if normal user and in private calendar view or admin */}
                                {(filterBy.includes('Private') || isAdmin) && <LinkContainer to="/calendars-v2">
                                    <Button type="primary" className="icon-button"
                                        icon={<PlusOutlined />} onClick={addNewSchedule}>Schedule
                                    </Button>
                                </LinkContainer>}
                                <div className="d-flex align-items-center ml-2" style={{ gap: 8 }}>
                                    <span style={{ fontWeight: 500 }}>Year</span>
                                    <Select
                                        value={selectedYear}
                                        onChange={(value) => {
                                            setSelectedYear(value);
                                            setManualScroll(false);
                                        }}
                                        style={{ width: 100 }}
                                        options={range(currentYear - 3, currentYear + 4).map(y => ({ value: y, label: y }))}
                                        className="mr-2"
                                    />
                                    <span style={{ fontWeight: 500, lineHeight: '14px' }}>Month</span>
                                    <Select
                                        value={selectedMonth}
                                        onChange={(value) => {
                                            setSelectedMonth(value);
                                            setManualScroll(false);
                                        }}
                                        style={{ width: 100 }}
                                        options={[
                                            { value: 1, label: 'January' },
                                            { value: 2, label: 'February' },
                                            { value: 3, label: 'March' },
                                            { value: 4, label: 'April' },
                                            { value: 5, label: 'May' },
                                            { value: 6, label: 'June' },
                                            { value: 7, label: 'July' },
                                            { value: 8, label: 'August' },
                                            { value: 9, label: 'September' },
                                            { value: 10, label: 'October' },
                                            { value: 11, label: 'November' },
                                            { value: 12, label: 'December' }
                                        ]}
                                    />
                                    <div className='gantt-nav-arrows ml-2'>
                                        <Tooltip title="Scroll left">
                                            <div 
                                                className={`gantt-nav-arrow gantt-nav-left`}
                                                onClick={() => handleMonthArrow('left')}
                                            >
                                                <LeftOutlined />
                                            </div>
                                        </Tooltip>
                                        <Tooltip title="Scroll right">
                                            <div 
                                                className={`gantt-nav-arrow gantt-nav-right`}
                                                onClick={() => handleMonthArrow('right')}
                                            >
                                                <RightOutlined />
                                            </div>
                                        </Tooltip>
                                    </div>
                                    {((selectedDates.length > 0) || (pinnedCalendars.length > 0)) && (
                                        <Button
                                            type="primary"
                                            style={{ marginLeft: 8 }}
                                            className="icon-button"
                                            onClick={() => {
                                                setSelectedDates([]);
                                                setPinnedCalendars([]);
                                            }}>
                                            Clear Selection & Unpin All
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div className="d-flex align-items-center pl-3">
                                <div className="justify-content-end w-separator">
                                    <InputGroup className="search-filter">
                                        <FormControl
                                            type="search"
                                            placeholder="Search calendars..."
                                            className=""
                                            size="sm"
                                            aria-label="Filter"
                                            onChange={(e) => setFilter(e.target.value)}
                                        />
                                        <InputGroup.Text id="basic-addon1"><SearchOutlined /></InputGroup.Text>
                                    </InputGroup>
                                </div>
                                <div className="filter-switches">
                                    <FilterSwitches
                                        filterItems={filterItems}
                                        filterBy={filterBy}
                                        setFilterBy={setFilterBy}
                                        exclusiveGroups={[['Private', 'Shared']]}
                                    />
                                </div>
                            </div>
                        </div>
                        <GanttView
                            calendars={ganttSchedules}
                            publicSchedules={publicSchedules}
                            onSearch={setFilter}
                            sortBy={sortBy}
                            setSortBy={setSortBy}
                            ganttTitle="Calendar"
                            selectedDates={selectedDates}
                            setSelectedDates={setSelectedDates}
                            pinnedCalendars={pinnedCalendars}
                            setPinnedCalendars={setPinnedCalendars}
                            manualScroll={manualScroll}
                            setManualScroll={setManualScroll}
                            startDate={startDate}
                            numberOfDays={numberOfDays}
                            scrollToDate={scrollToDate}
                            expandedScheduleId={expandedScheduleId}
                            editModeScheduleId={editModeScheduleId}
                            setEditModeScheduleId={setEditModeScheduleId}
                            setExpandedScheduleId={setExpandedScheduleId}
                            onScheduleUpdate={handleScheduleUpdate}
                            onCancelEdit={handleCancelEdit}
                            onDeleteSchedule={async (scheduleId, isShared) => {
                                if(isShared) {
                                    await publicScheduleModelData.delete(scheduleId, true, true);
                                    setPublicSchedules(publicSchedules => {
                                        const copy = { ...publicSchedules };
                                        delete copy[scheduleId];
                                        return copy;
                                    });
                                } else {
                                    await scheduleModelData.delete(scheduleId, true, true);
                                    setPrivateSchedules(privateSchedules => {
                                        const copy = { ...privateSchedules };
                                        delete copy[scheduleId];
                                        return copy;
                                    });
                                }
                            }}
                            filterItems={filterItems}
                            allTags={Array.from(allTags)}
                            originalSchedules={{ ...privateSchedules, ...publicSchedules }}
                            filterBy={filterBy}
                            selectedMonth={selectedMonth}
                            setSelectedMonth={setSelectedMonth}
                            selectedYear={selectedYear}
                            setSelectedYear={setSelectedYear}
                            setFilterBy={setFilterBy}
                        />
                    </div>
                ) : (
                    <LoadMessage message={"loading calendars..."} size={"lg"} />
                )}
            </>
        );
    }

    return (
        <div className="Calendar">
            {!isAuthenticating && isAuthenticated && renderCalendars()}
        </div>
    );
}

export default React.memo(CalendarV2); 